import { styled, useTheme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import MuiToolbar from "@mui/material/Toolbar";
import { tabsClasses } from "@mui/material/Tabs";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { SideMenuMobile } from "./SideMenuMobile";
import { MenuButton } from "./MenuButton";
import { ScrapChefIcon } from "components/common/navBar/ScrapChefIcon";
import { useState, useEffect, useRef } from "react";
import { LanguageSelector } from "components/settings/LanguageSelector";
import { UserMenu } from "./UserMenu/UserMenu";
import { AdminSettingsMenu } from "./UserMenu/AdminSettingsMenu";
import { Divider, Typography } from "@mui/material";
import { useTenantDataOptional } from "contexts/tenant";
import { useIsAdmin, useIsAdminPaused, useTenant } from "hooks/settings";
import { useIsMobile } from "hooks/useIsMobile";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { Link } from "react-router";
import { useSearchId } from "hooks/search";
import { SearchDetails } from "components/common/navBar/SearchDetails";
import { TenantSelectorMenu } from "./TenantSelectorMenu";
import { ProvideAvailableTenantMetadata } from "contexts/availableTenantMetadata";
import {
  useHasOverviewAccessOnAnyTenant,
  useHasAccessToMissionControl,
} from "hooks/permissions";
import { RequireNavigation } from "src/routes/authentication";

const Toolbar = styled(MuiToolbar)({
  display: "flex",
  flexDirection: "column",
  alignItems: "start",
  justifyContent: "center",
  gap: "12px",
  flexShrink: 0,
  [`& ${tabsClasses.list}`]: {
    gap: "8px",
    p: "8px",
    pb: 0,
  },
});

export const AppNavbar = () => {
  const theme = useTheme();
  const hasOverviewAccess = useHasOverviewAccessOnAnyTenant();
  const [open, setOpen] = useState(false);
  const tenantData = useTenantDataOptional();
  const isAdmin = useIsAdmin();
  const isAdminPaused = useIsAdminPaused();
  const hasAccessToMissionControl = useHasAccessToMissionControl();
  const tenant = useTenant();
  const searchId = useSearchId();
  const isMobile = useIsMobile();
  const hasMissionControl = useIsBehindFeatureFlag("show_in_mission_control");
  const appBarRef = useRef<HTMLDivElement>(null);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  useEffect(() => {
    const updateAppBarHeight = () => {
      if (appBarRef.current) {
        const height = appBarRef.current.getBoundingClientRect().height;
        document.documentElement.style.setProperty(
          "--app-bar-height",
          `${height}px`
        );
      }
    };

    updateAppBarHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateAppBarHeight();
    });

    if (appBarRef.current) {
      resizeObserver.observe(appBarRef.current);
    }

    window.addEventListener("resize", updateAppBarHeight);
    return () => {
      window.removeEventListener("resize", updateAppBarHeight);
      resizeObserver.disconnect();
    };
  }, [searchId, isMobile]);

  return (
    <AppBar
      ref={appBarRef}
      position="relative"
      sx={{
        boxShadow: 0,
        bgcolor: "black",
        borderBottom: "1px solid",
        borderColor: "divider",
        top: "var(--template-frame-height, 0px)",
        zIndex: 1300,
        maxHeight: isMobile && searchId !== null ? "none" : 64,
      }}
    >
      <Toolbar variant="regular">
        <Stack
          sx={{
            flexGrow: 1,
            gap: 1,
            width: "100%",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1,
              width: "100%",
              pt: { xs: 1, sm: 0 },
            }}
          >
            {hasOverviewAccess ? (
              <Link
                to={
                  hasMissionControl
                    ? "/mission-control"
                    : `/${tenant || "mission-control"}`
                }
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ justifyContent: "center", mr: { xs: 0, md: 11 } }}
                >
                  <ScrapChefIcon />
                </Stack>
              </Link>
            ) : (
              <Stack
                direction="row"
                spacing={1}
                sx={{ justifyContent: "center", mr: { xs: 0, md: 11 } }}
              >
                <ScrapChefIcon />
              </Stack>
            )}

            {searchId !== null && !isMobile ? (
              <>
                <Divider flexItem orientation="vertical" variant="fullWidth" />
                <SearchDetails id={searchId} />
              </>
            ) : null}
            <Stack
              direction="row"
              spacing={1}
              sx={{ ml: "auto" }}
              alignItems="center"
            >
              <Typography
                variant="h6"
                color="white"
                sx={{ pr: 2, display: { xs: "none", md: "block" } }}
              >
                {tenantData.long_name}
              </Typography>
              <AdminSettingsMenu isVisible={isAdmin || isAdminPaused} />
              {!isAdmin && !isAdminPaused && hasAccessToMissionControl ? (
                <ProvideAvailableTenantMetadata>
                  <TenantSelectorMenu />
                </ProvideAvailableTenantMetadata>
              ) : null}
              {hasOverviewAccess ? <LanguageSelector /> : null}
              <UserMenu />
              <RequireNavigation>
                <MenuButton
                  aria-label="menu"
                  onClick={toggleDrawer(true)}
                  sx={{
                    color: "white",
                    backgroundColor: theme.palette.grey[900],
                    "&:hover": {
                      backgroundColor: theme.palette.grey[600],
                    },
                    display: { xs: "auto", md: "none" },
                  }}
                >
                  <MenuRoundedIcon />
                </MenuButton>
                <SideMenuMobile open={open} toggleDrawer={toggleDrawer} />
              </RequireNavigation>
            </Stack>
          </Stack>
          {searchId !== null && isMobile ? (
            <Box sx={{ pb: 1 }}>
              <SearchDetails id={searchId} />
            </Box>
          ) : null}
        </Stack>
      </Toolbar>
    </AppBar>
  );
};
